<template>
  <DefaultCrudView
    @create-new="newPool"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="pools"
        class="elevation-1 compact-table"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:[`item.tenant_id`]="{ item }">
          {{ getTenantTitle(item.tenant_id) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="editPool(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="manageStatuses(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-checkbox-multiple-marked-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('manage_statuses') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="manageACLs(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-shield-account</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('pool_acl') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="manageLanes(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-reorder-horizontal</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('manage_lanes') }}</v-list-item-title>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item @click="deletePool(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-delete</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

      </v-data-table>

      <DefaultModal :title="getModalTitle()" ref="modal" @submit="save">
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

      <!-- Pool Status Modal -->
      <PoolStatus
        ref="poolStatusModal"
        mode="pool"
        :entityId="selectedPoolForStatus ? (selectedPoolForStatus.pool_id || selectedPoolForStatus.poolid || selectedPoolForStatus.id) : null"
        :entityTitle="selectedPoolForStatus ? selectedPoolForStatus.pool_title : ''"
      />


    </template>
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }

  /* Compact table cells to fit more actions */
  ::v-deep .compact-table .v-data-table__wrapper td,
  ::v-deep .compact-table .v-data-table__wrapper th {
    padding: 6px 8px !important;
  }
</style>

<script>
import { mapActions, mapGetters } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'
import PoolStatus from '@/views/job/poolstatus/PoolStatus.vue'

import AppActions from '@/store/app/actions-types'
import TenantActions from '@/store/core/tenant/actions-types'

import PoolAPI from '@/services/api/job/pool'

export default {
  name: "Pool",

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent,
    PoolStatus,
  },

  computed: {
    ...mapGetters('app', [
      'getIconForOperationSettingsItem',
      'getUserApplicationById'
    ]),

    breadcrumbs() {
      return [
        {
          text: this.$t('job_settings'),
          prependIcon: 'mdi-cog-outline',
          iconColor: 'primary',
          to: {
            name: "JobSettings",
            params: { appId: this.$route.params.appId }
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('pools'),
          prependIcon: 'mdi-view-sequential',
        },
      ]
    },

    appInstance() {
      return this.getUserApplicationById(this.$route.params.appId)
    },
    appConfig() {
      if (!this.appInstance || !this.appInstance.config) return {}
      return this.parseApplicationConfig(this.appInstance.config)
    },
    appTenantId() {
      return (
        this.appConfig.tenant_id ||
        this.appConfig.tenantId ||
        (this.appConfig.tenant && (this.appConfig.tenant.id || this.appConfig.tenant.tenant_id)) ||
        null
      )
    },

    fields() {
      const fields = [
        {
          key: 'tenant_id',
          name: 'tenant_id',
          label: this.$t('tenant'),
          type: 'select',
          value: this.selectedPool ? this.selectedPool.tenant_id : (this.appTenantId || ''),
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          items: this.tenants.map(tenant => ({
            text: tenant.title || tenant.tenant_title,
            value: tenant.tenant_id
          })),
          itemText: 'text',
          itemValue: 'value',
          cols: 12
        },
        {
          key: 'pool_title',
          name: 'pool_title',
          label: this.$t('pool_title'),
          type: 'text',
          value: this.selectedPool ? this.selectedPool.pool_title : '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 6
        },
        {
          key: 'color',
          name: 'color',
          label: this.$t('pool_color'),
          type: 'text',
          value: this.selectedPool ? (this.selectedPool.color || this.selectedPool.pool_color || '') : '',
          cols: 6
        },
        {
          key: 'pool_order',
          name: 'pool_order',
          label: this.$t('pool_order'),
          type: 'number',
          value: this.selectedPool ? this.selectedPool.pool_order : 0,
          rules: [v => v >= 0 || this.$t('must_be_positive')],
          cols: 4
        }
      ]

      return fields
    }
  },

  data() {
    return {
      loading: true,
      pools: [],
      options: {
        sortBy: ['pool_title'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: "create_pool",
          eventToEmit: "create-new",
        }
      ],
      tenants: [],
      selectedPool: null,
      selectedPoolForStatus: null,
      headers: [
        { text: this.$t('pool_title'), value: 'pool_title', sortable: true },
        { text: this.$t('tenant'), value: 'tenant_id', sortable: true },
        { text: this.$t('pool_order'), value: 'pool_order', sortable: true },
        { text: this.$t('actions'), value: 'actions', sortable: false }
      ]
    }
  },

  mounted() {
    this.selectedPool = null

    this[TenantActions.GET_ALL_TENANTS]()
      .then((tenants) => {
        this.tenants = tenants.items
      })
  },

  watch: {
    options: {
      handler () {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('core/tenant', [
      TenantActions.GET_ALL_TENANTS
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE
    ]),

    newPool() {
      this.selectedPool = null
      this.$refs.modal.openModal()
    },

    save() {
      // Add safety check for modal reference
      if (!this.$refs.modal) {
        console.error('Modal reference not found')
        return
      }

      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      // Collect data from dynamic form fields
      const formData = {}
      this.fields.forEach(field => {
        if (field.type === 'number') {
          formData[field.name] = field.value !== '' ? Number(field.value) : null
        } else {
          formData[field.name] = field.value
        }
      })

      // Calculate pool_key from pool_title
      if (formData.pool_title) {
        formData.pool_key = this.generatePoolKey(formData.pool_title)
      }

      // Validate tenant_id is present
      if (!formData.tenant_id) {
        this.$refs.modal.errorMessage = 'Tenant is required'
        this.$refs.modal.submitting = false
        return
      }

      let apiCall

      if (this.selectedPool) {
        // Handle different possible ID property names for updates
        const poolId = this.selectedPool.poolid || this.selectedPool.pool_id || this.selectedPool.id

        if (!poolId) {
          console.error('Pool ID not found in selected pool object:', this.selectedPool)
          this.$refs.modal.errorMessage = 'Pool ID not found'
          return
        }

        apiCall = PoolAPI.updatePool(poolId, formData)
      } else {
        // tenant_id is already in formData from the dropdown selection
        apiCall = PoolAPI.createPool(formData)
      }

      apiCall
        .then(() => {
          const message = this.selectedPool ? 'Pool updated successfully' : 'Pool created successfully'
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch((error) => {
          this.$refs.modal.submitting = false
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || 'An error occurred'
          }
        })
    },

    reloadData() {
      let options = {...this.$route.query}

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)

      // Filter pools by tenant from app config when available
      if (this.appTenantId) {
        options['filter[tenant]'] = this.appTenantId
      }

      this.loading = true
      PoolAPI.getAllPools(options)
        .then((result) => {
          this.pools = result.data.items || []
          this.itemsLength = result.data.total || 0
          this.loading = false
        })
        .catch(() => this.loading = false)
    },

    editPool(pool) {
      this.selectedPool = pool
      this.$refs.modal.openModal()
    },

    deletePool(pool) {
      if (confirm(this.$t('confirm_delete_pool'))) {
        // Handle different possible ID property names
        const poolId = pool.poolid || pool.pool_id || pool.id

        if (!poolId) {
          console.error('Pool ID not found in pool object:', pool)
          return
        }

        PoolAPI.deletePool(poolId)
          .then(() => {
            this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('Pool deleted successfully'))
            this.reloadData()
          })
          .catch((error) => {
            console.error('Error deleting pool:', error)
          })
      }
    },

    getTenantTitle(id) {
      let tenant = this.tenants.find(f => f.tenant_id == id)
      return tenant ? tenant.title : ''
    },

    getModalTitle() {
      return this.selectedPool ? this.$t('edit_pool') : this.$t('new_pool')
    },

    manageStatuses(pool) {
      this.selectedPoolForStatus = pool
      this.$refs.poolStatusModal.openModal()
    },

    // Parse app.config to JSON
    parseApplicationConfig(string) {
      try {
        return JSON.parse(string)
      } catch (e) {
        return {}
      }
    },
    manageLanes(pool) {
      const poolId = pool.pool_id || pool.poolid || pool.id
      this.$router.push({
        name: 'PoolLanes',
        params: {
          appId: this.$route.params.appId,
          poolId: poolId
        },
        query: {
          poolTitle: pool.pool_title
        }
      })
    },

    manageACLs(pool) {
      const poolId = pool.pool_id || pool.poolid || pool.id
      this.$router.push({
        name: 'PoolACL',
        params: {
          appId: this.$route.params.appId,
          poolId: poolId
        },
        query: {
          poolTitle: pool.pool_title
        }
      })
    },

    generatePoolKey(poolTitle) {
      if (!poolTitle) return ''

      return poolTitle
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
        .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
    }
  }
}
</script>
